import { useState } from "react";
import { dataCurta, type AgendamentoVisivel } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";

/**
 * Quando o item volta, e **a conta que produziu esse número** (RN-13).
 *
 * O que separa um SRS auditável de uma caixa-preta não é mostrar o resultado —
 * é mostrar a conta. "Volta em 7 dias" pede confiança; "7 dias, porque 6 × 1,2"
 * pode ser conferido contra `docs/srs.md` sem sair da tela.
 *
 * A conta não é refeita aqui: ela vem inteira do servidor, em `auditoria`. Se a
 * tela recalculasse, ela e o agendamento poderiam discordar — e o número que
 * aparece deixaria de ser o número que foi gravado.
 */

const NOME_DA_REGRA: Record<AgendamentoVisivel["auditoria"]["regra"], string> = {
  lapso: "errou, então o intervalo reinicia",
  primeira: "primeira revisão certa",
  segunda: "segunda revisão certa",
  multiplicacao: "intervalo anterior × fator de facilidade",
  dificil: "intervalo anterior × multiplicador do “com esforço”",
};

const NOME_DA_QUALIDADE: Record<number, string> = {
  0: "não lembrei",
  2: "errou",
  3: "com esforço",
  4: "acertou",
  5: "tranquilo",
};

/** "6 × 1,2 = 7 dias", ou o valor fixo quando não houve multiplicação. */
function contaDoIntervalo(a: AgendamentoVisivel["auditoria"]): string {
  if (a.multiplicadorAplicado === null) {
    return `${a.intervaloDepois} ${a.intervaloDepois === 1 ? "dia" : "dias"}, valor fixo desta etapa`;
  }
  const multiplicador = a.multiplicadorAplicado.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  return `${a.intervaloAntes} × ${multiplicador} = ${a.intervaloDepois} dias`;
}

function sinal(n: number): string {
  const valor = Math.abs(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n >= 0 ? "+" : "−"}${valor}`;
}

export function Agendamento({
  itemId,
  agendamento,
  aoAjustar,
}: {
  itemId: string;
  agendamento: AgendamentoVisivel;
  /** Ausente quando o item não aceita ajuste — cartão, que o usuário já avaliou. */
  aoAjustar?: (novo: AgendamentoVisivel) => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const a = agendamento.auditoria;
  const podeAjustar = Boolean(aoAjustar) && a.origem === "derivada";

  async function ajustar(valor: "dificil" | "facil" | null) {
    setOcupado(true);
    setErro(null);
    try {
      const r = await pedir("estudo.ajustar", { corpo: { itemId, ajuste: valor } });
      aoAjustar?.(r.agendamento);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível ajustar.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="agendamento" aria-label="Próxima revisão">
      {/* O intervalo e os dois toques, na largura deles: é o que o tour aponta (ADR 041). */}
      <div className="agendamento__topo" data-tour="volta">
        <p className="agendamento__quando">
          <span className="dado">{agendamento.frase}</span>
          <span className="tinta-suave"> · {dataCurta(agendamento.proximaRevisao)}</span>
        </p>

        {podeAjustar && (
          <ControlesDeAjuste
            atual={agendamento.ajuste}
            ocupado={ocupado}
            aoEscolher={(v) => void ajustar(v)}
          />
        )}
      </div>

      {erro && (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      )}

      {/*
        Recolhido por padrão: quem está estudando quer o número, não a conta.
        Mas ela está a um toque, sempre — e é isso que a torna auditável.
      */}
      <details className="agendamento__conta">
        <summary className="agendamento__resumo">Como esse intervalo saiu</summary>
        <dl className="lista-dados agendamento__detalhe">
          <Linha rotulo="Qualidade" valor={`${a.qualidade} — ${NOME_DA_QUALIDADE[a.qualidade] ?? "—"}`} />
          <Linha rotulo="De onde veio" valor={a.origem === "derivada" ? "derivada do acerto" : "sua avaliação"} />
          <Linha rotulo="Regra aplicada" valor={NOME_DA_REGRA[a.regra]} />
          <Linha rotulo="Intervalo" valor={contaDoIntervalo(a)} />
          <Linha
            rotulo="Fator de facilidade"
            valor={`${a.fatorAntes.toFixed(2)} ${sinal(a.ajusteDoFator)} = ${a.fatorDepois.toFixed(2)}${a.pisoAplicado ? " (segurado no piso)" : ""}`}
          />
          <Linha rotulo="Repetições seguidas" valor={`${a.repeticoesAntes} → ${a.repeticoesDepois}`} />
          <Linha rotulo="Lapsos ao todo" valor={String(agendamento.lapsos)} />
        </dl>
        <p className="campo__ajuda">
          A fórmula inteira, com exemplos resolvidos, está em <span className="dado">docs/srs.md</span>. Se algum
          número aqui não bater com ela, o defeito é do código.
        </p>
      </details>
    </section>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="linha linha--espalhada">
      <dt className="etiqueta">{rotulo}</dt>
      <dd className="lista-dados__valor dado">{valor}</dd>
    </div>
  );
}

/**
 * Os dois toques opcionais. Ficam **ao lado do intervalo**, não escondidos no
 * detalhe: eles corrigem justamente o número que está logo acima.
 *
 * Tocar de novo o que já está escolhido desfaz — voltar ao neutro não pode
 * exigir adivinhação.
 */
function ControlesDeAjuste({
  atual,
  ocupado,
  aoEscolher,
}: {
  atual: "dificil" | "facil" | null;
  ocupado: boolean;
  aoEscolher: (v: "dificil" | "facil" | null) => void;
}) {
  const opcoes = [
    { valor: "dificil" as const, rotulo: "Foi difícil" },
    { valor: "facil" as const, rotulo: "Foi fácil" },
  ];
  return (
    <div className="linha agendamento__ajuste" role="group" aria-label="Ajustar a dificuldade deste item">
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          className={
            atual === o.valor
              ? "botao botao--compacto botao--estreito"
              : "botao botao--secundario botao--compacto botao--estreito"
          }
          aria-pressed={atual === o.valor}
          disabled={ocupado}
          onClick={() => aoEscolher(atual === o.valor ? null : o.valor)}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}
